import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { NavigateFunction } from 'react-router-dom';
import { queryClient } from './queryClient';
import { showErrorToast, dismissAllToasts } from './toast';
import i18n from './i18n';
import { authKeys } from '../api/auth';
import { getCsrfToken, refreshCsrfToken } from './csrf';
import { broadcastAuthEvent } from './authChannel';
import { loginPathFor } from './loginRedirect';
import { translateBackendMessage } from './backendMessages';
import { isPublicAuthPath } from './publicPaths';
import { getDeviceId } from './deviceId';

const ECHO_BACKEND_URL = import.meta.env.VITE_ECHO_BACKEND_URL;

/**
 * Normalized API error with consistent shape for error handling
 */
export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
  code?: string;
  data?: unknown;
  isNetworkError: boolean;
}

/**
 * Create a normalized error from axios error for better stack traces and consistent shape
 */
function normalizeError(error: AxiosError<{ message?: string; error?: string }>): ApiError {
  const status = error.response?.status;
  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  const isNetworkError = !error.response;

  // Extract error message from response - prefer 'message' (human-readable) over 'error' (status text)
  const backendMessage = error.response?.data?.message || error.response?.data?.error;

  // Create a new error with better message
  let message: string;
  if (isNetworkError) {
    message = `Network error: Unable to reach server (${method} ${url})`;
  } else if (status && status >= 500) {
    message = backendMessage || `Server error ${status}: ${method} ${url}`;
  } else {
    message = backendMessage || error.message;
  }

  const normalizedError = new Error(message) as ApiError;
  normalizedError.name = isNetworkError ? 'NetworkError' : `HttpError${status}`;
  normalizedError.status = status;
  normalizedError.statusText = error.response?.statusText;
  normalizedError.url = url;
  normalizedError.method = method;
  normalizedError.code = error.code;
  normalizedError.data = error.response?.data;
  normalizedError.isNetworkError = isNetworkError;

  // Preserve the original stack but add our normalized info
  if (error.stack) {
    normalizedError.stack = `${normalizedError.name}: ${message}\n    at ${method} ${url}\n${error.stack
      .split('\n')
      .slice(1)
      .join('\n')}`;
  }

  return normalizedError;
}

// Store navigate function reference (set by AuthContext)
let navigateFn: NavigateFunction | null = null;

// The device-mismatch toast is not behind the had-session gate that makes the
// expiry toast fire once, so it dedupes itself: parallel 401s = one toast.
let lastDeviceMismatchToastAt = 0;

export const setNavigateFunction = (fn: NavigateFunction) => {
  navigateFn = fn;
};

/** Is the user on a page a signed-out visitor is supposed to be on? (see publicPaths.ts) */
const isOnPublicAuthPage = () => isPublicAuthPath(window.location.pathname);

export const axiosInstance = axios.create({
  baseURL: ECHO_BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: identify this device, add CSRF token to mutating requests
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // The session is bound to this device at login and must present it after,
    // on every request (lib/deviceId.ts)
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers['x-device-id'] = deviceId;
    }

    const method = config.method?.toLowerCase();

    // Add CSRF token to POST, PUT, DELETE, PATCH requests
    if (method && ['post', 'put', 'delete', 'patch'].includes(method)) {
      const token = getCsrfToken();
      if (token) {
        config.headers['x-csrf-token'] = token;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string }>) => {
    // Note: API error logging is handled by React Query's global error handler
    // Axios only handles 401/429 redirects - see queryClient.ts for logging logic

    // Handle 403 CSRF token errors - refresh token and retry once
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.message?.toLowerCase() || '';
      const isCsrfError = errorMessage.includes('csrf') || errorMessage.includes('token');

      // Check if this is a CSRF error and we haven't retried yet
      const originalRequest = error.config;
      if (isCsrfError && originalRequest && !(originalRequest as { _csrfRetry?: boolean })._csrfRetry) {
        (originalRequest as { _csrfRetry?: boolean })._csrfRetry = true;

        try {
          // Refresh CSRF token and retry the request
          await refreshCsrfToken();
          const newToken = getCsrfToken();
          if (newToken) {
            originalRequest.headers['x-csrf-token'] = newToken;
          }
          return axiosInstance(originalRequest);
        } catch {
          // If refresh fails, reject with original error
          return Promise.reject(normalizeError(error));
        }
      }
    }

    // Handle 401 Unauthorized globally
    if (error.response?.status === 401) {
      // "Session expired" is only true if this tab WAS signed in: the session
      // cache holds a user. A cold visit to a protected URL, a fresh load after
      // the session lapsed, or the probe on the login page also land here and
      // stay silent. Said once — the cache is nulled below, so the other 401s a
      // page may have in flight find no user.
      const hadSession = !!queryClient.getQueryData(authKeys.session());
      // One 401 is not like the others: the API found a live session, it just
      // is not this device's. That is worth saying even on a cold load (the
      // cookies prove there WAS a session), or the user re-logs in blaming
      // expiry — while a probe on the login page itself stays quiet.
      const isDeviceMismatch = error.response.data?.message === 'Device mismatch';
      if (isDeviceMismatch && !isOnPublicAuthPage()) {
        if (Date.now() - lastDeviceMismatchToastAt > 5000) {
          lastDeviceMismatchToastAt = Date.now();
          showErrorToast(i18n.t('toast.deviceMismatch', 'This session belongs to another device. Please log in again.'));
        }
      } else if (hadSession) {
        showErrorToast(i18n.t('toast.sessionExpired', 'Session expired. Please log in again.'));
      }
      if (hadSession) {
        // Same gate: only a tab that had a session has news for the others. The
        // cookies are shared, so their sessions are just as dead — tell them
        // now rather than letting each find out on its next request.
        broadcastAuthEvent({ type: 'signed-out', reason: 'expired' });
      }

      // Skip the redirect if already on a public page (e.g. a failed login
      // attempt, or the session probe on the login page itself)
      if (!isOnPublicAuthPage()) {
        // The login URL remembers where the session ended (?redirectTo=...)
        const loginPath = loginPathFor(window.location.pathname + window.location.search);

        // Clear session cache so ProtectedRoute redirects
        queryClient.setQueryData(authKeys.session(), null);

        // Redirect to login
        if (navigateFn) {
          navigateFn(loginPath);
        } else {
          window.location.href = loginPath;
        }
      }
      return Promise.reject(normalizeError(error));
    }

    // Handle 429 Rate Limit globally
    if (error.response?.status === 429) {
      // Clear all toasts
      dismissAllToasts();

      // If on login page, let Login.tsx handle the error display (avoid duplicate toasts)
      if (isOnPublicAuthPage()) {
        return Promise.reject(normalizeError(error));
      }

      // The login URL remembers where the session ended (?redirectTo=...)
      const loginPath = loginPathFor(window.location.pathname + window.location.search);

      // Clear session cache
      queryClient.setQueryData(authKeys.session(), null);

      // Navigate to login
      if (navigateFn) {
        navigateFn(loginPath);
      } else {
        window.location.href = loginPath;
      }

      showErrorToast(
        error.response.data?.message
          ? translateBackendMessage(error.response.data.message)
          : i18n.t('toast.rateLimited', 'Too many requests. Please try again later.')
      );

      return Promise.reject(normalizeError(error));
    }

    // Normalize all errors for consistent shape and better stack traces
    return Promise.reject(normalizeError(error));
  }
);
